import {inject, Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';

import {Observable} from 'rxjs';

import * as PL2 from '@common/utils/dist/models/models-pl2.js';

import {Action} from '@actions/action';
import {
  CreateSectionAction,
  SectionTarget,
} from '@actions/create-section.action';
import {SharedDataTableType} from '@constants/app.config';
import {UserStore} from '@stores/user.store';
import {defaultDataTableOpts} from '@utils/utils';

export interface AddTableSectionInput {
  title: string;
  columns: string[];
  /** One cell per column; empty cells are left for students */
  rows: string[][];
}

/**
 * Data table cells for a table card, laid out like the app's default table: the header row
 * is row 0, sheet 0, cell ids k<n>_0; empty cells have no value.
 */
export function dataTableCells(
  input: AddTableSectionInput,
  userId: string,
  ts = String(Date.now()),
): {[key: string]: PL2.DataTableCell} {
  const cells: {[key: string]: PL2.DataTableCell} = {};
  let n = 0;
  [input.columns, ...input.rows].forEach((row, r) =>
    row.forEach((value, c) => {
      const id = `k${++n}_0`;
      cells[id] = {
        id,
        value: value || null,
        r,
        c,
        s: 0,
        isA: 'y',
        uId: userId,
        ts,
      };
    }),
  );
  return cells;
}

/** Adds a table card proposed by the lesson chat to the end of the current lesson. */
@Injectable({providedIn: 'root'})
export class AddTableSectionToLessonAction implements Action<
  AddTableSectionInput,
  PL2.AnySectionEntry
> {
  private _createSectionAction = inject(CreateSectionAction);
  private _translate = inject(TranslateService);
  private _userStore = inject(UserStore);

  execute(
    input: AddTableSectionInput,
    target?: SectionTarget,
  ): Observable<PL2.AnySectionEntry> {
    const userId = this._userStore.state().userId;
    const dataSetName = this._translate.instant(
      'shared-data-table-scatter-plot.dataSet',
    );
    return this._createSectionAction.execute({
      partialEntry: {
        n:
          input.title ||
          this._translate.instant('shared-data-table-table.nDefault'),
        dat: {
          ...dataTableCells(input, userId),
          ...defaultDataTableOpts(userId, dataSetName),
        },
        dT: SharedDataTableType.DataTable,
      } as Partial<PL2.SharedDataTableSectionEntry>,
      entryType: PL2.EntryType.SharedDataTableSection,
      index: Number.MAX_SAFE_INTEGER,
      scope: PL2.SectionScope.Group,
      target,
    });
  }
}
